/**
 * PARTS — who you buy from, and what the customer pays.
 *
 * Two things the shop actually needs from this screen, and they are the
 * two the old system buried under a lookup:
 *
 *   VENDORS, in the order you ring them. A counter that answers and has
 *   the part beats a cheaper one that does not, and the order is knowledge
 *   the shop has that the software cannot work out.
 *
 *   THE PRICE BANDS. A $20 part and a $900 part do not carry the same
 *   margin, and these twelve rows are what turns cost into the customer's
 *   number on every quote. They came across from the old system and were
 *   missing from the first import, which meant every part quietly fell to
 *   the 1.35 fallback.
 *
 * WHAT IS NOT HERE: the live catalog lookup. There is a PartsTech key in
 * the environment and no screen behind it. That is the next thing, and
 * pretending otherwise with a search box that returns nothing would be
 * worse than the honest gap.
 *
 * That sentence, and the card at the bottom that says it to the shop, now
 * live in src/domain/gaps.ts with the pattern that would mean they have
 * come true. Not because the wording was wrong, but because the OTHER
 * sentence on that card went false without anything failing. See the
 * comment above the card.
 */

import { db } from "../data/client.ts";
import { sellPrice, FALLBACK_MULTIPLIER, type Tier } from "../domain/pricing.ts";
import { esc, money, mount } from "../ui/dom.ts";
import { shopWords } from "../ui/words.ts";
import { cap, anA } from "../domain/trade.ts";
import type { Role } from "../domain/permissions.ts";
import { gapsOn, lastChecked, NOT_BUILT_GROUP } from "../domain/gaps.ts";

type Vendor = {
  id: string; name: string; phone: string | null; url: string | null;
  account_ref: string | null; rank: number | null; active: boolean; notes: string | null;
};

const BOSS: Role[] = ["admin", "supervisor"];

/**
 * What this screen still does not do, read off the registry rather than
 * typed. Nothing to show means no card, because a heading over nothing
 * reads as a screen that failed to load rather than as good news.
 */
function notBuiltCard(): string {
  const gaps = gapsOn("parts", shopWords().hasPartsCatalog);
  if (!gaps.length) return "";
  const when = lastChecked(gaps);
  return `<div class="card">
      <div class="card-head">${esc(NOT_BUILT_GROUP)}</div>
      ${gaps.map((g) => `<div class="wt-trap">${esc(g.detail ?? g.body)}</div>`).join("")}
      ${when ? `<div class="pad"><p class="quiet">Read against the code on ${esc(when)}.</p></div>`
        : ""}
    </div>`;
}

/** What a band does to a real number, said in the shop's own terms. */
function bandRow(t: Tier & { id: string }, mayEdit: boolean): string {
  const to = t.cost_to == null ? "and up" : `to ${money(Number(t.cost_to))}`;
  // A worked example beats a multiplier. Nobody quotes in multipliers.
  const example = Number(t.cost_from) > 0 ? Number(t.cost_from) : 10;
  return `
    <div class="row">
      <div class="row-main">
        <div class="row-title"><span class="num">${money(Number(t.cost_from))} ${esc(to)}</span></div>
        <div class="row-sub">${esc(cap(anA(shopWords().partLabel)))} costing ${money(example)} is quoted at
          ${money(example * Number(t.multiplier))}</div>
      </div>
      <div class="row-side">
        <div class="amount">&times;${Number(t.multiplier).toFixed(2)}</div>
        ${mayEdit ? `<div class="when">${Number(t.min_margin) ? `min ${money(Number(t.min_margin))}` : ""}</div>` : ""}
      </div>
    </div>`;
}

export async function partsPage(role: Role = "viewer"): Promise<void> {
  const mayEdit = BOSS.includes(role);

  mount(`<div class="page">
    <header class="page-head"><a class="back" href="#/more">&larr; Everything else</a>
      <h1>${esc(cap(shopWords().partsLabel))}</h1>
      <p>Who you buy from, and what the customer pays for it.</p></header>
    <div id="partsbody"><div class="loading">Reading&hellip;</div></div>
  </div>`);

  const host = document.getElementById("partsbody");
  if (!host) return;

  try {
    const sb = await db();
    const [v, t] = await Promise.all([
      sb.from("vendors").select("id,name,phone,url,account_ref,rank,active,notes")
        .order("rank", { ascending: true }).order("name"),
      sb.from("markup_tiers").select("id,cost_from,cost_to,multiplier,min_margin")
        .order("cost_from", { ascending: true }),
    ]);
    if (v.error) throw new Error(v.error.message);

    const vendors = (v.data ?? []) as unknown as Vendor[];
    const tiers = (t.data ?? []) as unknown as Array<Tier & { id: string }>;

    host.innerHTML = `
      <div class="card">
        <div class="card-head">Suppliers &middot; ${vendors.length}</div>
        ${vendors.length ? vendors.map((x) => `
          <div class="row">
            <div class="row-main">
              <div class="row-title">
                <span class="num">${esc(x.name)}</span>
                ${x.active ? "" : `<span class="pill s-cancelled">not used</span>`}
                ${x.account_ref ? `<span class="pill">acct ${esc(x.account_ref)}</span>` : ""}
              </div>
              <div class="row-sub">
                ${x.phone ? `<a href="tel:${esc(x.phone)}">${esc(x.phone)}</a>` : "no number"}
                ${x.url ? `<span class="dot">&middot;</span><a href="${esc(x.url)}"
                  target="_blank" rel="noopener">their site</a>` : ""}
              </div>
              ${x.notes ? `<div class="row-note">${esc(x.notes)}</div>` : ""}
            </div>
            <div class="row-side"><div class="when">#${x.rank ?? "—"}</div></div>
          </div>`).join("") : `<div class="empty">No suppliers on file.</div>`}
      </div>

      <div class="card">
        <div class="card-head">What the customer pays &middot; ${tiers.length} bands</div>
        ${tiers.length ? tiers.map((x) => bandRow(x, mayEdit)).join("") : `
          <div class="empty bad">No bands. Every ${esc(shopWords().partLabel)} is falling back to
            &times;${FALLBACK_MULTIPLIER}, which means every quote is guessing.</div>`}
        <div class="pad">
          <p class="quiet">The band with the highest floor that still contains the cost wins.
            A cost outside every band falls back to &times;${FALLBACK_MULTIPLIER}.
            The customer never sees your cost &mdash; only the number these produce.</p>
        </div>
      </div>

      <div class="card">
        <div class="card-head">Try it</div>
        <div class="pad">
          <div class="tryrow">
            <label class="field"><span>${esc(cap(anA(shopWords().partLabel)))} costs you</span>
              <input id="try-cost" type="number" step="0.01"></label>
            <div class="tryout"><span>The customer pays</span><b id="try-out">&mdash;</b></div>
          </div>
        </div>
      </div>

      ${/**
          ─── THESE TWO SENTENCES WERE TYPED HERE, AND ONE WENT FALSE ────

          The card used to end "Core deposits and returns are not tracked
          here." By 2026-09-18 the returns half was wrong, and wrong about
          the half the law cares about: garage.html asks the customer for
          their old parts at the authorization, the answer is stored on the
          authorization and printed on the invoice. The shop was being told
          its software did not do a thing it does.

          Same fault as the process page's "What is still missing" card,
          which told Anthony on 2026-09-17 that his inspection sheet was not
          built. A claim that something does NOT exist has nothing that goes
          with the feature when the feature lands, so it never stops
          rendering.

          src/domain/gaps.ts carries each one with the pattern that would
          mean it is finished, and tools/gap-check.mjs fails npm run check
          the moment one comes true. The trade gate stays here because it is
          about who is reading: a lock shop is never going to look a door up
          in a vehicle catalog. */
        notBuiltCard()}`;

    // A calculator beats a paragraph explaining bands.
    const cost = document.getElementById("try-cost") as HTMLInputElement | null;
    const out = document.getElementById("try-out");
    cost?.addEventListener("input", () => {
      const c = Number(cost.value);
      if (out) out.textContent = c > 0 ? money(sellPrice(tiers, c)) : "—";
    });
  } catch (e) {
    host.innerHTML = `<div class="card"><div class="empty bad">${
      esc(e instanceof Error ? e.message : String(e))}</div></div>`;
  }
}
